import { useMemo } from 'react';

import { type CreateVerdict, type SelectionFilterFn } from '../types/selection-field.types';
import { type SelectionGroup, type SelectionOption } from '../types/selection-option.types';

/**
 * How many rows the panel will draw before it asks the operator to narrow.
 *
 * Not virtualization, deliberately: a windowed list needs a measured container
 * height, and the panel's ceiling is a Radix measurement that is `0` forever in
 * jsdom — so every windowed implementation renders nothing in the suite. Rows
 * are variable-height here too (description, icon, group headings), and a
 * windowed checkbox `role="group"` has no `aria-setsize` contract to restore
 * "3 of 250" with. A hundred rows plus a filter input that is already focused is
 * the honest answer; a quarter of a thousand checkboxes is not.
 */
export const SELECTION_RENDER_CAP = 100;

export interface SelectionFilterSection {
  /** `null` for the section above every heading: created values, then ungrouped. */
  group: SelectionGroup | null;
  options: readonly SelectionOption[];
}

export interface UseSelectionFilterInput {
  options: readonly SelectionOption[];
  groups?: readonly SelectionGroup[];
  /** Raw, exactly as typed. */
  term: string;
  selected: ReadonlySet<string>;
  filterFn?: SelectionFilterFn;
  isCreatable?: boolean;
  validateCreate?: (rawTerm: string) => CreateVerdict;
  /** Already translated — the hook holds no catalogue so it stays pure. */
  duplicateMessage?: string;
}

export interface UseSelectionFilter {
  sections: readonly SelectionFilterSection[];
  /** Flat, in render order — what the roving keyboard stop walks. */
  visibleOptions: readonly SelectionOption[];
  /** Chosen values that are on no option list, in `value` order. */
  createdValues: readonly string[];
  hiddenCount: number;
  isEmpty: boolean;
  createCandidate: CreateVerdict | null;
}

const ARABIC_MARKS = /[ً-ْٰـ]/g;
const ALEF_VARIANTS = /[أإآٱ]/g;

/**
 * Case- and diacritic-insensitive, so a search behaves the way an Arabic
 * operator expects: "احمد" finds "أَحْمَد".
 *
 * Only the shapes that are genuinely the same letter typed differently are
 * folded — harakat, tatweel, the four alef forms, alef maqsura, ta marbuta and
 * the two hamza carriers. Anything more aggressive starts matching words that
 * are not the same word.
 */
function normalize(text: string): string {
  return text
    .toLocaleLowerCase()
    .replace(ARABIC_MARKS, '')
    .replace(ALEF_VARIANTS, 'ا')
    .replace(/ى/g, 'ي')
    .replace(/ة/g, 'ه')
    .replace(/ؤ/g, 'و')
    .replace(/ئ/g, 'ي');
}

function defaultFilter(option: SelectionOption, normalizedTerm: string): boolean {
  return (
    normalize(option.label).includes(normalizedTerm) ||
    (option.description !== undefined && normalize(option.description).includes(normalizedTerm))
  );
}

function sectionize(
  options: readonly SelectionOption[],
  groups: readonly SelectionGroup[] | undefined,
): SelectionFilterSection[] {
  const known = new Set(groups?.map((group) => group.id));
  const ungrouped = options.filter(
    (option) => option.groupId === undefined || !known.has(option.groupId),
  );

  const sections: SelectionFilterSection[] = [];
  if (ungrouped.length > 0) sections.push({ group: null, options: ungrouped });

  for (const group of groups ?? []) {
    const members = options.filter((option) => option.groupId === group.id);
    // A heading with nothing under it reads as a broken list, not as an empty
    // category — the group goes when its last option does.
    if (members.length > 0) sections.push({ group, options: members });
  }

  return sections;
}

/**
 * The render cap, applied.
 *
 * A chosen value is never invisible, even when the chosen values alone
 * outnumber the cap — being unable to SEE what you would be unchecking is a
 * worse failure than a long list.
 */
function capTo(
  matched: readonly SelectionOption[],
  selected: ReadonlySet<string>,
): readonly SelectionOption[] {
  if (matched.length <= SELECTION_RENDER_CAP) return matched;

  const chosen = matched.filter((option) => selected.has(option.value));
  const room = Math.max(SELECTION_RENDER_CAP - chosen.length, 0);
  const rest = matched.filter((option) => !selected.has(option.value)).slice(0, room);
  const keptValues = new Set([...chosen, ...rest].map((option) => option.value));

  return matched.filter((option) => keptValues.has(option.value));
}

/**
 * Filtering, grouping, the render cap and the create candidate — everything the
 * panel needs to know *what* to draw, with no opinion about how.
 */
export function useSelectionFilter({
  options,
  groups,
  term,
  selected,
  filterFn,
  isCreatable = false,
  validateCreate,
  duplicateMessage = '',
}: UseSelectionFilterInput): UseSelectionFilter {
  const createdValues = useMemo(() => {
    const known = new Set(options.map((option) => option.value));
    return [...selected].filter((value) => !known.has(value));
  }, [options, selected]);

  const allOptions = useMemo(
    () => [...createdValues.map((value) => ({ value, label: value })), ...options],
    [createdValues, options],
  );

  // Neither of the next two is hand-memoized, and that is deliberate: both can
  // legitimately return the array they were GIVEN (an empty term filters
  // nothing; a short list is never capped), and React Compiler cannot preserve a
  // `useMemo` whose result may be one of its own inputs — it reports
  // `preserve-manual-memoization` and then skips optimizing the whole hook.
  // Leaving them plain lets the compiler memoize them properly instead.
  const trimmedTerm = term.trim();
  const matched =
    trimmedTerm === ''
      ? allOptions
      : filterFn !== undefined
        ? // The RAW term reaches an injected filter: normalisation is this
          // hook's default behaviour, not a contract a caller's own matcher
          // must live with.
          allOptions.filter((option) => filterFn(option, term))
        : allOptions.filter((option) => defaultFilter(option, normalize(trimmedTerm)));

  const kept = capTo(matched, selected);
  const hiddenCount = matched.length - kept.length;

  const sections = useMemo(() => sectionize(kept, groups), [groups, kept]);

  const createCandidate = useMemo((): CreateVerdict | null => {
    if (!isCreatable) return null;

    const trimmed = term.trim();
    if (trimmed === '') return null;
    if (validateCreate !== undefined) return validateCreate(term);

    // Against the FULL list, never the filtered one: a custom `filterFn` can
    // legitimately hide an exact match, and creating a duplicate of a row that
    // is merely off screen is the trap this guards.
    const normalized = normalize(trimmed);
    const exists = allOptions.some(
      (option) =>
        option.value === trimmed ||
        normalize(option.value) === normalized ||
        normalize(option.label) === normalized,
    );

    return exists
      ? { status: 'rejected', reason: duplicateMessage }
      : { status: 'ok', value: trimmed };
  }, [allOptions, duplicateMessage, isCreatable, term, validateCreate]);

  return {
    sections,
    visibleOptions: kept,
    createdValues,
    hiddenCount,
    isEmpty: kept.length === 0,
    createCandidate,
  };
}
